import React, { useCallback, useEffect, useState } from "react";
import FoundryButton from "../components/foundry/FoundryButton";
import FoundryCard from "../components/foundry/FoundryCard";
import { searchEntraUsers } from "../features/internal-access/api";
import type { EntraUser } from "../features/internal-access/types";
import { readWorkCenterJson } from "./workCenterApi";

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;
export type WorkCenterUser = { id: string; displayName: string; email: string; isActive: boolean };

export default function WorkCenterUsersSettings({ apiFetch }: { apiFetch: ApiFetch }) {
  const [users, setUsers] = useState<WorkCenterUser[]>([]);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<EntraUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const load = useCallback(async () => {
    try {
      const data = await readWorkCenterJson(await apiFetch("/api/admin/workcenter/users?includeInactive=true"));
      setUsers(data.items || []);
      setError("");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Gebruikers laden mislukt."); }
  }, [apiFetch]);
  useEffect(() => { void load(); }, [load]);
  async function search(event: React.FormEvent) {
    event.preventDefault();
    if (query.trim().length < 2) return;
    setBusy(true);
    try {
      setMatches((await searchEntraUsers(apiFetch, query.trim())).filter(user => user.accountEnabled !== false));
      setSearched(true);
      setError("");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Entra zoeken mislukt."); }
    finally { setBusy(false); }
  }
  async function add(id: string) {
    setBusy(true);
    try {
      await readWorkCenterJson(await apiFetch("/api/admin/workcenter/users", { method: "POST", body: JSON.stringify({ id }) }));
      setMatches([]);
      setSearched(false);
      setQuery("");
      await load();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Gebruiker toevoegen mislukt."); }
    finally { setBusy(false); }
  }
  async function toggle(user: WorkCenterUser) {
    setBusy(true);
    try {
      await readWorkCenterJson(await apiFetch(`/api/admin/workcenter/users/${encodeURIComponent(user.id)}`, { method: "PATCH", body: JSON.stringify({ isActive: !user.isActive }) }));
      await load();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Gebruiker wijzigen mislukt."); }
    finally { setBusy(false); }
  }
  return <FoundryCard className="min-w-0">
    <h2 className="text-xl font-black">Work Center-gebruikers</h2>
    <p className="mt-2 text-sm text-slate-500">Voeg medewerkers toe vanuit Entra. Actieve gebruikers zijn beschikbaar bij Taak toewijzen.</p>
    <form onSubmit={search} className="mt-5 flex gap-2"><input aria-label="Zoek Entra-gebruiker" value={query} onChange={event => setQuery(event.target.value)} minLength={2} placeholder="Naam of e-mailadres" className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-transparent px-3 py-2 text-sm dark:border-slate-700" /><FoundryButton type="submit" disabled={busy || query.trim().length < 2}>Zoeken</FoundryButton></form>
    {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}
    {matches.length > 0 && <div className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-white/10 dark:border-white/10">{matches.map(user => <div key={user.id} className="flex items-center justify-between gap-3 p-3"><div className="min-w-0"><p className="font-bold">{user.displayName}</p><p className="truncate text-xs text-slate-500">{user.mail || user.userPrincipalName}</p></div><FoundryButton type="button" disabled={busy} onClick={() => add(user.id)}>Toevoegen</FoundryButton></div>)}</div>}
    {searched && !busy && !matches.length && !error && <p className="mt-4 text-sm text-slate-500">Geen medewerkers gevonden.</p>}
    <div className="mt-6 divide-y divide-slate-200 dark:divide-white/10">{users.map(user => <div key={user.id} className="flex items-center justify-between gap-3 py-3"><div className="min-w-0"><p className="font-bold">{user.displayName} {!user.isActive && <span className="text-xs text-slate-500">Inactief</span>}</p><p className="truncate text-xs text-slate-500">{user.email}</p></div><FoundryButton type="button" variant="secondary" disabled={busy} onClick={() => toggle(user)}>{user.isActive ? "Deactiveren" : "Activeren"}</FoundryButton></div>)}{users.length === 0 && !error && <p className="py-4 text-sm text-slate-500">Nog geen Work Center-gebruikers toegevoegd.</p>}</div>
  </FoundryCard>;
}
